import { AppDispatch, RootState } from "./index";
import { AuthActions } from "./auth-slice";
import { UIActions } from "./ui-slice";
import { t } from "../i18n";

// Renew this long before the ID token actually runs out.
const REFRESH_MARGIN_MS = 60_000;

// Focus, the poll and a save can all notice an expired token at the same
// moment - they share one request instead of each sending their own.
let refreshInFlight: Promise<string> | null = null;

const requestNewToken = async (
  refreshToken: string | null,
  dispatch: AppDispatch
): Promise<string> => {
  let response: Response | undefined;
  if (refreshToken) {
    try {
      response = await fetch(
        "https://securetoken.googleapis.com/v1/token?key=" +
          window.firebaseKey,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body:
            "grant_type=refresh_token&refresh_token=" +
            encodeURIComponent(refreshToken),
        }
      );
    } catch (err) {
      // Waking from sleep is exactly when this runs and when the network isn't
      // back yet. That says nothing about the session, so it must not end it.
      throw new Error(t.noConnection);
    }

    if (response.ok) {
      const data = await response.json();
      dispatch(
        AuthActions.login({
          token: data.id_token,
          expirationTime: data.expires_in,
          localId: data.user_id,
          refreshToken: data.refresh_token,
        })
      );
      return data.id_token;
    }

    // Only a 400 means Firebase refused the refresh token itself. A 5xx or a
    // 429 is the service having a bad moment, and the token is still good.
    if (response.status !== 400) {
      throw new Error(t.somethingWentWrong);
    }
  }

  dispatch(AuthActions.logout());
  dispatch(UIActions.error({ message: t.sessionExpired }));
  throw new Error(t.sessionExpired);
};

export const refreshAuthToken = (refreshToken: string | null) => {
  return (dispatch: AppDispatch) => {
    if (!refreshInFlight) {
      refreshInFlight = requestNewToken(refreshToken, dispatch).finally(() => {
        refreshInFlight = null;
      });
    }
    return refreshInFlight;
  };
};

// Resolves to a token that is good to use right now, renewing it first if
// needed, and rejects with a user-facing message if there is no way to get one.
// Every database call goes through this, so nothing depends on the timer in
// App having fired on time.
export const getFreshToken = (force = false) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const { token, refreshToken } = getState().auth;
    const expiresAt = Number(localStorage.getItem("expirationTime"));
    if (!force && token && expiresAt - Date.now() > REFRESH_MARGIN_MS) {
      return token;
    }
    return dispatch(refreshAuthToken(refreshToken));
  };
};
